import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";

/**
 * GradesComparator — multi-curriculum grade → admit-band comparator.
 *
 * Replaces the A-Level-only `GradesCalculator`. Speaks eight school-
 * leaving systems: A-Level, IB, US SAT+GPA+AP, Indian CBSE/ISC,
 * French Bac, German Abitur, Spanish EvAU, Chinese Gaokao.
 *
 * Behaviour:
 *  • Fetches the curriculum registry from the backend once and the
 *    uni's editorial admit bands once.
 *  • Renders a curriculum picker + inputs matching that curriculum's
 *    schema.
 *  • Debounces `POST /api/curriculum/evaluate` on input change to
 *    surface a verdict + position-in-band + UCAS-tariff equivalent.
 *  • When the curriculum yields a UCAS equivalent AND the parent
 *    passed `onTariffChange`, notifies so the parent histogram can
 *    still drop the "YOU" marker for UK unis.
 *  • For non-UK / non-UCAS curricula, renders a compact admit-band
 *    bar chart in-place (p10 · p50 · p90 with a YOU marker).
 */

// Curriculum-specific "helper text" surfaces conversion cheats so a
// family can sanity-check the verdict rather than treating it as a
// black box.
const CURRICULUM_HELP = {
  a_level: "UCAS tariff: A*=56, A=48, B=40, C=32 pts",
  ib: "Higher-Level 7=56 UCAS pts · Standard-Level 7=28 · TOK+EE bonus up to 12",
  us: "Non-official UCAS approx: GPA + SAT + AP courses. Non-UK unis use their own admit band.",
  in: "Best-of-4 % from Class XII. ~95%=A*A*A, ~85%=AAA, ~75%=ABB",
  fr: "Note /20. Mention TB (≥16) ≈ AAA; Mention B (14-15.99) ≈ AAB",
  de: "1.0 = best; 4.0 = pass. 1.0 ≈ A*A*A, 2.0 ≈ AAB, 3.0 ≈ BBB",
  es: "/14 scale (bachillerato + EvAU). 13+ ≈ AAA, 10 ≈ AAB",
  cn: "Provincial score /750. Non-UK anchor — no UCAS equivalent published.",
};

export default function GradesComparator({ uni, onTariffChange }) {
  const [registry, setRegistry] = useState(null);
  const [bands, setBands] = useState({});
  // ── Curriculum resolution priority ─────────────────────────────
  //   1. `?curriculum=ib` in the URL — highest priority so a
  //      counsellor can share a link that opens on the right picker
  //      for the family they're helping. Overrides localStorage
  //      only for THIS render; localStorage is not written unless
  //      the visitor then interacts.
  //   2. `localStorage.wua_curriculum` — the visitor's remembered
  //      pick.
  //   3. `/curriculum/recommend` — quiet geolocation fallback
  //      (handled by the useEffect below).
  const _urlCurriculum = (() => {
    if (typeof window === "undefined") return null;
    try {
      const p = new URLSearchParams(window.location.search).get("curriculum");
      return p ? p.trim().toLowerCase() : null;
    } catch {
      return null;
    }
  })();
  const [curriculumId, setCurriculumId] = useState(() => {
    if (_urlCurriculum) return _urlCurriculum;
    try { return localStorage.getItem("wua_curriculum") || null; }
    catch { return null; }
  });
  // Track whether the user has ever chosen a curriculum. Once they
  // have, geolocation must never override their pick — even across
  // fresh reloads on a different network. A deep-link IS an
  // explicit choice too (the counsellor decided for them).
  const [userPicked, setUserPicked] = useState(() => {
    if (_urlCurriculum) return true;
    try { return !!localStorage.getItem("wua_curriculum"); }
    catch { return false; }
  });
  const [inputs, setInputs] = useState({});
  const [result, setResult] = useState(null);
  const debounceRef = useRef(null);

  // Fetch registry + admit bands once per uni.
  useEffect(() => {
    let cancelled = false;
    api.get("/curriculum/registry").then((r) => {
      if (!cancelled) setRegistry(r.data?.curricula || []);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Quiet geolocation fallback — only when the visitor hasn't picked
  // a curriculum themselves. localStorage always wins so a US student
  // studying in the UK, or an IB kid on holiday in Delhi, never sees
  // their picker flip on them. The endpoint reads CDN-forwarded
  // country + Accept-Language; no IP is stored server-side.
  useEffect(() => {
    if (userPicked || curriculumId) return;
    let cancelled = false;
    api.get("/curriculum/recommend").then((r) => {
      if (cancelled) return;
      const suggested = r.data?.curriculum || "a_level";
      setCurriculumId(suggested);
    }).catch(() => {
      if (!cancelled) setCurriculumId("a_level");
    });
    return () => { cancelled = true; };
  }, [userPicked, curriculumId]);

  useEffect(() => {
    if (!uni?.id) return;
    let cancelled = false;
    api.get(`/universities/${uni.id}/admit-bands`).then((r) => {
      if (!cancelled) setBands(r.data?.bands || {});
    }).catch(() => setBands({}));
    return () => { cancelled = true; };
  }, [uni?.id]);

  // Reset inputs when curriculum changes (default per-input value).
  const activeCurriculum = useMemo(
    () => (registry || []).find((c) => c.id === curriculumId) || null,
    [registry, curriculumId],
  );
  useEffect(() => {
    if (!activeCurriculum) return;
    const defaults = {};
    for (const f of activeCurriculum.inputs || []) {
      defaults[f.key] = f.default;
    }
    setInputs(defaults);
    // Only persist the curriculum id once the user has explicitly
    // picked it. Otherwise a geolocation-defaulted `a_level` would
    // masquerade as an explicit choice on the next visit, defeating
    // the localStorage-wins rule.
    if (userPicked) {
      try { localStorage.setItem("wua_curriculum", curriculumId); } catch {}
    }
    // Broadcast so cards elsewhere on the page (e.g. Advanced Results
    // `<CurriculumAdmitBadge>`) refresh to the new curriculum.
    try {
      window.dispatchEvent(new CustomEvent("wua-curriculum-change", { detail: curriculumId }));
    } catch {}
  }, [curriculumId, activeCurriculum, userPicked]);

  // Debounced evaluate on input change.
  useEffect(() => {
    if (!activeCurriculum) return;
    if (Object.keys(inputs).length === 0) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      api.post("/curriculum/evaluate", {
        curriculum: curriculumId,
        inputs,
        uni_id: uni?.id || null,
      }).then((r) => {
        setResult(r.data || null);
        if (r.data?.ucas_tariff_equivalent != null && onTariffChange) {
          onTariffChange(r.data.ucas_tariff_equivalent);
        }
      }).catch(() => setResult(null));
    }, 250);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [curriculumId, inputs, activeCurriculum, uni?.id, onTariffChange]);

  if (!registry || !activeCurriculum) {
    return (
      <div className="border border-[#002147]/10 bg-[#F3F1EC] p-4 md:p-5 mb-6 h-32 animate-pulse"
           data-testid="grades-comparator-loading" />
    );
  }

  const verdictColour = result?.tier === "above" ? "#059669"
                      : result?.tier === "match" ? "#4C1D95"
                      : "#B45309";
  const band = result?.admit_band || bands[activeCurriculum.admit_band_key] || null;
  const ucas = result?.ucas_tariff_equivalent;
  const scalar = result?.scalar;

  // Grade-4-adds-breadth microcopy: A-Level users who sit a 4th
  // subject often panic-optimise it. But UCAS tariff caps at the
  // best 3 — a 4th grade adds admissions signal (breadth, subject
  // fit) but zero tariff. Only surface the note when the tariff is
  // already at or above the target uni's p90, so the message is
  // "you're past the ceiling here, relax about the 4th slot" —
  // never "your 4th slot is worthless" (which would be wrong for
  // low-tariff users still climbing).
  const _grade4Set = activeCurriculum.id === "a_level"
    && inputs.grade_4
    && inputs.grade_4 !== "—";
  const _atP90 = band?.p90 != null
    && ucas != null
    && ucas >= band.p90;
  const grade4BreadthHint = _grade4Set && _atP90;

  return (
    <div
      className="border border-[#002147]/10 bg-[#F3F1EC] p-4 md:p-5 mb-6"
      data-testid="grades-comparator"
    >
      {/* Header row: label + curriculum picker */}
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <div className="font-mono text-[10px] uppercase tracking-widest text-[#7C3AED]">
          How my grades compare
        </div>
        <select
          value={curriculumId || ""}
          onChange={(e) => {
            setCurriculumId(e.target.value);
            setUserPicked(true);
          }}
          className="border border-[#002147]/20 bg-white px-2.5 py-1.5 font-mono text-[11px] text-[#002147] focus:outline-none focus:border-[#7C3AED]"
          data-testid="grades-comparator-curriculum-picker"
          aria-label="Grading system"
        >
          {registry.map((c) => (
            <option key={c.id} value={c.id}>
              {c.flag}  {c.label}
            </option>
          ))}
        </select>
      </div>

      {/* Inputs row + verdict column */}
      <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-4 items-start">
        <div className="flex gap-2 flex-wrap">
          {(activeCurriculum.inputs || []).map((f) => (
            <InputField
              key={f.key}
              field={f}
              value={inputs[f.key]}
              onChange={(v) => setInputs((prev) => ({ ...prev, [f.key]: v }))}
            />
          ))}
        </div>

        <div className="text-right min-w-[180px]">
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#002147]/50">
            {ucas != null ? "UCAS equivalent" : activeCurriculum.scale_label}
          </div>
          <div
            className="font-serif text-2xl text-[#002147] leading-none"
            data-testid="grades-comparator-scalar"
          >
            {ucas != null
              ? <>{ucas}<span className="text-[#002147]/50 text-sm ml-1">pts</span></>
              : (scalar != null ? scalar.toString() : "—")}
          </div>

          {result?.verdict_label && (
            <>
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#002147]/50 mt-2">
                Verdict
              </div>
              <div
                className="font-serif text-lg leading-tight mt-0.5"
                style={{ color: verdictColour }}
                data-testid="grades-comparator-verdict"
              >
                {result.verdict_label}
              </div>
              {result.position_pct != null && (
                <div className="font-mono text-[10px] text-[#002147]/60 mt-1">
                  Position {result.position_pct.toFixed(0)} / 100 in the admit band
                </div>
              )}
            </>
          )}
          {/* Grade-4 breadth microcopy — sits below the verdict when
              present, but must render even for UCAS-derived bands
              where the /evaluate endpoint returns no `verdict_label`
              (e.g. Oxford A-Level, where the histogram is authoritative
              and no editorial verdict exists). */}
          {grade4BreadthHint && (
            <div
              data-testid="grades-comparator-grade4-breadth-hint"
              className="mt-2 text-[11px] font-mono text-[#059669] bg-emerald-50 border border-emerald-200 rounded-sm px-2 py-1.5 leading-snug text-left max-w-[220px] ml-auto"
              title="UCAS tariff sums only your best 3 A-Levels — a 4th grade signals subject breadth to admissions tutors but never lifts the tariff number."
            >
              4th grade adds breadth, not tariff — you're already at the ceiling here.
            </div>
          )}
        </div>
      </div>

      {/* Admit-band bar (only for non-UCAS curricula or when we have
          a specific editorial band that adds value on top of the UK
          UCAS histogram). */}
      {band && (
        <AdmitBandBar band={band} scalar={scalar} />
      )}

      {!band && result && (
        <div className="mt-3 text-[11px] font-mono text-[#B45309]"
             data-testid="grades-comparator-no-band">
          No editorial admit band on file for {uni?.name || "this uni"} in
          {" "}{activeCurriculum.label || activeCurriculum.id} yet.
          {ucas != null && " Falling back to the UCAS-tariff histogram below."}
        </div>
      )}

      <div className="mt-2 text-[10px] text-[#002147]/50 leading-relaxed">
        {CURRICULUM_HELP[activeCurriculum.id]}
        {band?.confidence === "editorial" && (
          <span className="ml-1 italic">Band is editorial — treat as guidance, not a cutoff.</span>
        )}
      </div>
    </div>
  );
}


function InputField({ field, value, onChange }) {
  const tid = `grades-comparator-input-${field.key}`;
  if (field.type === "select") {
    return (
      <label className="flex flex-col">
        <span className="font-mono text-[10px] uppercase tracking-wider text-[#002147]/50 mb-0.5">
          {field.label}
        </span>
        <select
          value={value ?? field.default}
          onChange={(e) => onChange(e.target.value)}
          className="border border-[#002147]/20 bg-white px-3 py-2 font-mono text-sm text-[#002147] focus:outline-none focus:border-[#7C3AED]"
          data-testid={tid}
        >
          {field.options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      </label>
    );
  }
  if (field.type === "select_int") {
    const range = [];
    for (let i = field.min; i <= field.max; i++) range.push(i);
    return (
      <label className="flex flex-col">
        <span className="font-mono text-[10px] uppercase tracking-wider text-[#002147]/50 mb-0.5">
          {field.label}
        </span>
        <select
          value={value ?? field.default}
          onChange={(e) => onChange(parseInt(e.target.value, 10))}
          className="border border-[#002147]/20 bg-white px-3 py-2 font-mono text-sm text-[#002147] focus:outline-none focus:border-[#7C3AED]"
          data-testid={tid}
        >
          {range.map((n) => <option key={n} value={n}>{n}</option>)}
        </select>
      </label>
    );
  }
  // number
  return (
    <label className="flex flex-col">
      <span className="font-mono text-[10px] uppercase tracking-wider text-[#002147]/50 mb-0.5">
        {field.label}
      </span>
      <input
        type="number"
        value={value ?? field.default}
        min={field.min}
        max={field.max}
        step={field.step || 1}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="border border-[#002147]/20 bg-white px-3 py-2 font-mono text-sm text-[#002147] focus:outline-none focus:border-[#7C3AED] w-28"
        data-testid={tid}
      />
    </label>
  );
}


/**
 * AdmitBandBar — mini horizontal bar showing p10 · p50 · p90 admit
 * band with a YOU marker at the user's scalar position. Handles
 * inverted (Abitur) direction transparently by inverting min/max.
 */
function AdmitBandBar({ band, scalar }) {
  const { p10, p50, p90, p25, p75, direction = "desc", unit, note, confidence } = band;
  if (p10 == null || p50 == null || p90 == null) return null;

  // Bar geometry: pick the min/max so both directions read L→R "weak
  // to strong". For asc (Abitur), that means max=lo, min=hi.
  const [barLo, barHi] = direction === "asc" ? [p10, p90] : [p10, p90];
  // Range of what we render — pad a little beyond p10/p90 so a user
  // slightly outside the band still shows up.
  const [displayMin, displayMax] = (() => {
    if (direction === "asc") {
      // For asc, p90 < p10 numerically. Left edge = weakest (p10), right = strongest (p90).
      const pad = Math.abs(p10 - p90) * 0.25;
      return [p90 - pad, p10 + pad];
    }
    const pad = Math.abs(p90 - p10) * 0.25;
    return [p10 - pad, p90 + pad];
  })();

  const project = (v) => {
    const range = displayMax - displayMin;
    if (range === 0) return 50;
    // For asc, higher-original = weaker = leftward. Invert.
    const t = direction === "asc"
      ? (displayMax - v) / range
      : (v - displayMin) / range;
    return Math.max(0, Math.min(100, t * 100));
  };

  const posP10 = project(p10);
  const posP50 = project(p50);
  const posP90 = project(p90);
  const posP25 = p25 != null ? project(p25) : null;
  const posP75 = p75 != null ? project(p75) : null;
  const posYou = scalar != null ? project(scalar) : null;

  return (
    <div className="mt-4" data-testid="grades-comparator-admit-band">
      <div className="flex items-center justify-between mb-1">
        <div className="font-mono text-[10px] uppercase tracking-widest text-[#002147]/50">
          Admit band · p10 → p90 {unit ? `(${unit})` : ""}
          {p25 != null && p75 != null && (
            <span className="ml-2 normal-case tracking-normal text-[#4C1D95]/70">
              · typical p25–p75 {p25}–{p75}
            </span>
          )}
        </div>
        {confidence && (
          <div
            className={`font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm ${
              confidence === "official"
                ? "bg-emerald-50 text-emerald-800 border border-emerald-200"
                : "bg-amber-50 text-amber-800 border border-amber-200"
            }`}
          >
            {confidence}
          </div>
        )}
      </div>
      <div className="relative h-8 rounded-md bg-white border border-[#002147]/10">
        {/* Wider p10 → p90 envelope — lighter shade */}
        <div
          className="absolute inset-y-1 bg-gradient-to-r from-[#F5F0FF] via-[#7C3AED]/20 to-[#4C1D95]/25 rounded"
          style={{ left: `${posP10}%`, width: `${Math.max(0, posP90 - posP10)}%` }}
        />
        {/* Tighter p25 → p75 "typical admit" band — darker fill so
            families can read the exact CDS mid-50% window at a glance. */}
        {posP25 != null && posP75 != null && (
          <div
            className="absolute inset-y-1 bg-[#4C1D95]/35 rounded"
            style={{ left: `${posP25}%`, width: `${Math.max(0, posP75 - posP25)}%` }}
            title={`Typical admit ${p25}–${p75} ${unit || ""}`}
            data-testid="grades-comparator-typical-band"
          />
        )}
        {/* p50 line */}
        <div
          className="absolute inset-y-0 w-[2px] bg-[#4C1D95]/90"
          style={{ left: `${posP50}%` }}
          title={`Modal admit: ${p50}${unit ? " " + unit : ""}`}
        />
        <div
          className="absolute text-[9px] font-mono text-[#4C1D95] -translate-x-1/2"
          style={{ left: `${posP50}%`, top: "-14px" }}
        >
          MODAL {p50}
        </div>
        {/* YOU marker */}
        {posYou != null && (
          <>
            <div
              className="absolute inset-y-[-4px] w-[3px] bg-[#B45309] rounded"
              style={{ left: `${posYou}%` }}
              data-testid="grades-comparator-you-marker"
            />
            <div
              className="absolute text-[10px] font-mono font-semibold text-[#B45309] -translate-x-1/2"
              style={{ left: `${posYou}%`, bottom: "-16px" }}
            >
              YOU {scalar}
            </div>
          </>
        )}
      </div>
      <div className="flex justify-between mt-4 text-[10px] font-mono text-[#002147]/50">
        <span>p10 {p10}</span>
        <span>p90 {p90}</span>
      </div>
      {note && (
        <div className="mt-1 text-[10px] text-[#002147]/45 italic">
          {note}
        </div>
      )}
    </div>
  );
}
